import {
  OpmInputDataSource,
  StaticCaseSelectionOptions,
} from '@app/shared/models/contracts/enums/shared-enums';
import {
  CaseDto,
  ForecastInputDto,
  MarketValueDlomDto,
  ProjectDto,
} from '@app/shared/models/contracts/project-dto';
import { getOpmInputsFromCapitalStructure } from './getOpmInputsFromCapitalStructure';
import { InstrumentDefinitionWithId } from '../store/capital-structure-slice-selectors';
import { Pwerm2CalculationResultsDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { Pwerm2InputCalculationResultsDto } from '@app/shared/models/contracts/pwerm2-input-calculation-results-dto';

export interface OpmInputsFromPwerm {
  hypotheticalDebtWhenIpo: number;
  ipoProbability: number;
  ipoCostsOfEv: number;
  secondarySaleCostsOfEv: number;
  ipoDiscount: number;
  equitySoldInIpo: number;
  postIpoSaleDlom: number;
  financingCosts: number;
  marketValueDloms: MarketValueDlomDto[];
  totalNetDebt: number;
  operationalFreeCashFlow: number;
  operationalEv: number;
}

const getPwermForecastInputs = (
  caseItem: CaseDto,
  forecastDate: string
): ForecastInputDto | undefined =>
  caseItem.forecasts.find((f) => f.forecastYear === forecastDate)?.inputs;

export const getOpmInputsFromPwerm = (
  project: ProjectDto,
  calculationResult: Pwerm2CalculationResultsDto,
  inputCalculationResult: Pwerm2InputCalculationResultsDto = {} as Pwerm2InputCalculationResultsDto
) => {
  const instrumentDefinitions = Object.values(project.capitalStructures)[0].instrumentDefinitions;
  const valuedInstruments: InstrumentDefinitionWithId[] = Object.entries(instrumentDefinitions)
    .filter(([_, instrument]) => instrument.shouldBeValued)
    .map(([instrumentId, instrument]) => ({ instrumentId, ...instrument }));

  const getCasesWithSpecificForecast = (forecastDate: string): CaseDto[] =>
    project.pwermInput.cases.filter((caseItem) =>
      caseItem.forecasts.find((forecast) => forecast.forecastYear === forecastDate)
    );

  const getCaseWeightedAverageFromPwerm = (getValue: (caseItem: CaseDto) => number | undefined) => {
    // find case via selectedCaseId and return its value
    if (
      project.opmInput.selectedCaseId &&
      project.opmInput.selectedCaseId !== StaticCaseSelectionOptions.CaseWeighted
    ) {
      const projectCases = project.pwermInput.cases;
      const selectedCase = projectCases.find((c) => c.caseId === project.opmInput.selectedCaseId);
      return selectedCase ? getValue(selectedCase) ?? 0 : 0;
    }

    return project.pwermInput.cases.reduce((weightedValue, caseItem) => {
      const value = getValue(caseItem) ?? 0;

      return weightedValue + ((caseItem.probability ?? NaN) * value) / 100;
    }, 0);
  };

  const getCaseWeightedAverageFromPwermAtForecast = (
    forecastDate: string,
    getValue: (caseItem: CaseDto) => number | undefined
  ) => {
    const projectCases = project.pwermInput.cases;

    if (
      project.opmInput.selectedCaseId &&
      project.opmInput.selectedCaseId !== StaticCaseSelectionOptions.CaseWeighted
    ) {
      const selectedCase = projectCases.find((c) => c.caseId === project.opmInput.selectedCaseId);
      return selectedCase ? getValue(selectedCase) ?? 0 : 0;
    }

    const relevantCases = getCasesWithSpecificForecast(forecastDate);
    const totalProbabilityAcrossRelevantCases = relevantCases.reduce(
      (totalProbability, caseItem) => totalProbability + (caseItem.probability ?? 0),
      0
    );

    if (totalProbabilityAcrossRelevantCases === 0) {
      return 0;
    }

    const weightedAverage = relevantCases.reduce((weightedValue, caseItem) => {
      const value: number = getValue(caseItem) ?? 0;
      return weightedValue + (caseItem.probability ?? NaN) * value;
    }, 0);

    const normalisedWeightedAverage = weightedAverage / totalProbabilityAcrossRelevantCases;
    return normalisedWeightedAverage;
  };

  const getIpoProbabilityFromPwerm = (forecastDate: string): number =>
    getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) => getPwermForecastInputs(caseItem, forecastDate)?.probabilityOfIpo
    );

  const getHypotheticalDebtWhenIpoFromPwerm = (forecastDate: string): number =>
    getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) => getPwermForecastInputs(caseItem, forecastDate)?.hypotheticalDebtAtIpo
    );

  const getIpoCostsOfEvFromPwerm = (forecastDate: string): number =>
    getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) => getPwermForecastInputs(caseItem, forecastDate)?.ipoTransactionCosts
    );

  const getSecondarySaleCostsOfEvFromPwerm = (forecastDate: string): number =>
    getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) =>
        getPwermForecastInputs(caseItem, forecastDate)?.tradeSaleTransactionCost
    );

  const getIpoDiscountFromPwerm = (forecastDate: string): number =>
    getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) => getPwermForecastInputs(caseItem, forecastDate)?.ipoDiscount
    ) ?? 0;

  const getEquitySoldInIpoFromPwerm = (forecastDate: string): number =>
    getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) =>
        getPwermForecastInputs(caseItem, forecastDate)?.percentOfEquitySoldInIpo
    );

  const getPostIpoSaleDlomFromPwerm = (forecastDate: string): number =>
    getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) =>
        getPwermForecastInputs(caseItem, forecastDate)?.dlomForPostIpoSaleRestrictions
    );

  const getOperationalFreeCashFlowFromPwerm = (forecastDate: string): number =>
    getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) => getPwermForecastInputs(caseItem, forecastDate)?.operationalFreeCashFlow
    );

  const getOperationalFreeCashFlowAverageFromPwerm = (forecastDate: string) => {
    const timesToExit = calculationResult.timesToExit;

    if (timesToExit === undefined) {
      return 0;
    }

    const individualTimeToExit = timesToExit[forecastDate];

    if (typeof individualTimeToExit !== 'number') {
      return 0;
    }

    const totalOperationalFreeCashFlow = Object.entries(timesToExit).reduce((total, [date, _]) => {
      if (date && date.localeCompare(forecastDate) <= 0) {
        return total + (getOperationalFreeCashFlowFromPwerm(date) || 0);
      }
      return total;
    }, 0);

    return totalOperationalFreeCashFlow / individualTimeToExit;
  };

  const getTotalNetDebtFromPwerm = (forecastDate: string): number =>
    getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) =>
        inputCalculationResult.cases?.[caseItem.caseId]?.forecastDates?.[forecastDate]?.totalNetDebt
    );

  const getInitialTotalNetDebtFromPwerm = () =>
    getCaseWeightedAverageFromPwerm(
      (caseItem: CaseDto) =>
        inputCalculationResult.cases?.[caseItem.caseId]?.valuationDate?.totalNetDebt
    );

  const getEvFromPwerm = (forecastDate: string): number => {
    // when calculationResult is empty (as would be the case if /pwerm failed), we return the
    // existing operationalEv, as this is calculated in the frontend using the /pwerm response
    if (Object.keys(calculationResult).length === 0) {
      return (
        project.opmInput.perYearInputs.find((input) => input.forecastDate === forecastDate)
          ?.operationalEv ?? 0
      );
    }
    const evAtForecast = getCaseWeightedAverageFromPwermAtForecast(
      forecastDate,
      (caseItem: CaseDto) => {
        const forecast = calculationResult.waterfall
          ? Object.values(calculationResult.waterfall.cases[caseItem.caseId].years).find(
              (f) => f.exitDate === forecastDate
            )
          : null;
        return forecast?.weightedExitEv ?? 0;
      }
    );
    return evAtForecast;
  };

  const getEvAverageFromPwerm = (forecastDate: string): number => {
    const { getEvOrEquityValueFromCapitalStructure } = getOpmInputsFromCapitalStructure(project);
    const startingEv =
      project.opmInput.evValueSource !== OpmInputDataSource.Override
        ? getEvOrEquityValueFromCapitalStructure()
        : project.opmInput.evValue ?? 0;

    const evAtForecastDate = getEvFromPwerm(forecastDate);
    const evAverage = (Number(startingEv) + evAtForecastDate) / 2;
    return evAverage;
  };

  const getFinancingCostsFromPwerm = (forecastDate: string): number => {
    // when calculationResult is empty (as would be the case if /pwerm failed), we return the
    // existing financingCosts, as this is calculated in the frontend using the /pwerm response
    if (Object.keys(calculationResult).length === 0) {
      return (
        project.opmInput.perYearInputs.find((input) => input.forecastDate === forecastDate)
          ?.financingCosts ?? 0
      );
    }
    const operationalAverageFreeCashFlow = getOperationalFreeCashFlowAverageFromPwerm(forecastDate);
    const evAverage = getEvAverageFromPwerm(forecastDate);
    return evAverage === 0
      ? 0
      : Number(((operationalAverageFreeCashFlow / evAverage) * 100).toFixed(8)) ?? 0;
  };

  const getPwermForecastInstrumentDlom = (
    caseItem: CaseDto,
    forecastDate: string,
    instrumentId: string,
    trancheId: Nullable<string>
  ) =>
    caseItem.forecasts
      .find((f) => f.forecastYear === forecastDate)
      ?.dlom.find((d) => d.instrumentId === instrumentId && d.trancheId === trancheId)?.value ?? 0;

  const getControlPremiumForDlocByInstrumentFromPwerm = (): Dictionary<number> => {
    const controlPremiumForDloc = project.valuationInputs.minorityDiscounts.controlPremiumForDloc;
    if (controlPremiumForDloc) {
      return controlPremiumForDloc;
    }
    const dlocValue = project.valuationInputs.minorityDiscounts.dloc ?? 0;
    const result: Dictionary<number> = {};
    valuedInstruments.forEach((instrument) => {
      result[instrument.instrumentId] =
        instrument.type === 'ShareholderLoanNotes' || instrument.type === 'PreferredShares'
          ? 0
          : dlocValue;
    });
    return result;
  };

  const getMarketVolatilityFromPwerm = (): Nullable<number> =>
    project.valuationInputs.minorityDiscounts.marketVolatility;

  const getPeerSetVolatilityFromPwerm = (): Nullable<number> =>
    project.valuationInputs.minorityDiscounts.peerSetVolatility;

  const getMarketValueDlomsFromPwerm = (forecastDate: string): MarketValueDlomDto[] =>
    valuedInstruments.map((instrument) => {
      return {
        instrumentId: instrument.instrumentId,
        instrumentDlom:
          instrument.payoutLogic && instrument.payoutLogic?.length > 0
            ? null
            : getCaseWeightedAverageFromPwermAtForecast(forecastDate, (caseItem: CaseDto) =>
                getPwermForecastInstrumentDlom(
                  caseItem,
                  forecastDate,
                  instrument.instrumentId,
                  null
                )
              ),
        instrumentDlomSource: OpmInputDataSource.FromPWERM,
        trancheDloms:
          instrument.payoutLogic?.map((tranche) => {
            return {
              trancheId: tranche.id,
              dlom: getCaseWeightedAverageFromPwermAtForecast(forecastDate, (caseItem: CaseDto) =>
                getPwermForecastInstrumentDlom(
                  caseItem,
                  forecastDate,
                  instrument.instrumentId,
                  tranche.id
                )
              ),
              dlomSource: OpmInputDataSource.FromPWERM,
            };
          }) ?? [],
      };
    });

  const getOpmValuesFromPwerm = (forecastDate: Nullable<string>): OpmInputsFromPwerm => {
    return {
      hypotheticalDebtWhenIpo: forecastDate ? getHypotheticalDebtWhenIpoFromPwerm(forecastDate) : 0,
      ipoProbability: forecastDate ? getIpoProbabilityFromPwerm(forecastDate) : 0,
      ipoCostsOfEv: forecastDate ? getIpoCostsOfEvFromPwerm(forecastDate) : 0,
      secondarySaleCostsOfEv: forecastDate ? getSecondarySaleCostsOfEvFromPwerm(forecastDate) : 0,
      ipoDiscount: forecastDate ? getIpoDiscountFromPwerm(forecastDate) : 0,
      equitySoldInIpo: forecastDate ? getEquitySoldInIpoFromPwerm(forecastDate) : 0,
      postIpoSaleDlom: forecastDate ? getPostIpoSaleDlomFromPwerm(forecastDate) : 0,
      financingCosts: forecastDate ? getFinancingCostsFromPwerm(forecastDate) : 0,
      marketValueDloms: forecastDate ? getMarketValueDlomsFromPwerm(forecastDate) : [],
      totalNetDebt: forecastDate ? getTotalNetDebtFromPwerm(forecastDate) : 0,
      operationalFreeCashFlow: forecastDate ? getOperationalFreeCashFlowFromPwerm(forecastDate) : 0,
      operationalEv: forecastDate ? getEvFromPwerm(forecastDate) : 0,
    };
  };

  const getAllOpmValuesFromPwerm = (forecastDates: Nullable<string>[]): OpmInputsFromPwerm[] =>
    forecastDates.map(getOpmValuesFromPwerm);

  return {
    getControlPremiumForDlocByInstrumentFromPwerm,
    getMarketVolatilityFromPwerm,
    getPeerSetVolatilityFromPwerm,
    getAllOpmValuesFromPwerm,
    getOpmValuesFromPwerm,
    getInitialTotalNetDebtFromPwerm,
    getFinancingCostsFromPwerm,
  };
};
